import axios from 'axios';

/**
 * Get site operating hours from site configuration
 * @param {string} relevantUrl - The base URL for the site
 * @returns {Promise<{openHours: string, closeHours: string} | null>}
 */
export const getSiteOperatingHours = async (relevantUrl) => {
  try {
    // Get the full site configuration
    const response = await axios.post(`${relevantUrl}/admin/get_config_json`);
    const configData = response.data;
    
    if (!configData) {
      console.warn('No configuration data received');
      return null;
    }

    // Look for operating hours in the Site params section
    let openHours = null;
    let closeHours = null;

    // Check the "Site params" section which contains open_hours and close_hours
    const siteParams = configData['Site params'];
    if (Array.isArray(siteParams)) {
      // Find open_hours parameter
      const openParam = siteParams.find(param => param.name === 'open_hours');
      // Find close_hours parameter  
      const closeParam = siteParams.find(param => param.name === 'close_hours');

      if (openParam?.value) {
        openHours = openParam.value;
        console.log('Found site open hours:', openHours);
      }
      if (closeParam?.value) {
        closeHours = closeParam.value;
        console.log('Found site close hours:', closeHours);
      }
    }

    // Default hours if not found in configuration
    const defaultHours = {
      openHours: openHours || '08:00',
      closeHours: closeHours || '22:00'
    };

    console.log('Site operating hours loaded:', defaultHours);
    return defaultHours;

  } catch (error) {
    console.error('Error fetching site operating hours:', error);
    return {
      openHours: '08:00',  // Default fallback
      closeHours: '22:00'  // Default fallback
    };
  }
};

/**
 * Check if the current time is within site operating hours
 * @param {string} relevantUrl - The base URL for the site
 * @returns {Promise<boolean>}
 */
export const isSiteOpen = async (relevantUrl) => {
  try {
    const hours = await getSiteOperatingHours(relevantUrl);
    if (!hours) return true; // Default to open if can't determine

    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes(); // Current time in minutes

    // Parse open hours (format: "HH:MM")
    const [openHour, openMin] = hours.openHours.split(':').map(Number);
    const openTime = openHour * 60 + openMin;

    // Parse close hours (format: "HH:MM")
    const [closeHour, closeMin] = hours.closeHours.split(':').map(Number);
    const closeTime = closeHour * 60 + closeMin;

    // Check if current time is within operating hours
    if (closeTime > openTime) {
      // Normal day (e.g., 07:00 - 18:00)
      return currentTime >= openTime && currentTime <= closeTime;
    } else {
      // Overnight operation (e.g., 22:00 - 06:00)
      return currentTime >= openTime || currentTime <= closeTime;
    }

  } catch (error) {
    console.error('Error checking if site is open:', error);
    return true; // Default to open if error
  }
};

/**
 * Get formatted operating hours string for display
 * @param {string} relevantUrl - The base URL for the site
 * @returns {Promise<string>}
 */
export const getOperatingHoursDisplay = async (relevantUrl) => {
  try {
    const hours = await getSiteOperatingHours(relevantUrl);
    if (!hours) return 'Operating hours not configured';

    return `${hours.openHours} - ${hours.closeHours}`;
  } catch (error) {
    console.error('Error formatting operating hours:', error);
    return 'Operating hours unavailable';
  }
};

export default {
  getSiteOperatingHours,
  isSiteOpen,
  getOperatingHoursDisplay
};
